import {Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import { NgbDate, NgbDateAdapter, NgbDateParserFormatter, NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {DateTime} from 'luxon';
import {SbLocalDate, SbLocalDateTime} from 'src/app/services/api/api-date-time';
import {SbDateISOParserFormatter} from 'src/app/services/date-time/date-formatter-iso.service';
import {SbDateStructAdapter} from 'src/app/services/date-time/ngb-sb-date-struct-adapter.service';
import {UtilService} from 'src/app/services/util.service';
import {ErrorService} from 'src/app/services/error.service';
import {ToasterService, ToastType} from '../../../services/toaster/toaster.service';
import {TranslateService} from '@ngx-translate/core';
import { NgbTranslatedDatepicker } from '../../../services/date-time/ngb-datepicker-translated.service';

@Component({
  selector: 'sb-local-date-input',
  templateUrl: './sb-local-date-input.component.html',
  styleUrls: ['./sb-local-date-input.component.scss'],
  providers: [
    { provide: NgbDatepickerI18n, useClass: NgbTranslatedDatepicker },
    { provide: NgbDateParserFormatter, useClass: SbDateISOParserFormatter },
    { provide: NgbDateAdapter, useClass: SbDateStructAdapter },
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SbLocalDateInputComponent), multi: true },
  ],
  standalone: false,
})
export class SbLocalDateInputComponent implements OnInit, ControlValueAccessor {
  constructor(
    private errorHandler: ErrorService,
    public util: UtilService,
    private eRef: ElementRef,
    private toastHandler: ToasterService,
    public translate: TranslateService
  ) {}

  @Input('minDate')
  set minDate(val: SbLocalDateTime) {
    if (val && val.value) {
      this._minDate = new SbLocalDateTime(
        val.value.set({
          day: val.value.day,
          month: val.value.month,
          year: val.value.year,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
      );
    }
    if (this._minDate && this._minDate.value) {
      this._minDateStruct = {
        year: this._minDate.value.year,
        month: this._minDate.value.month,
        day: this._minDate.value.day,
      };
    }
  }

  get minDate(): SbLocalDateTime {
    return this._minDate;
  }

  @Input('maxDate')
  set maxDate(val: SbLocalDateTime) {
    if (val && val.value) {
      this._maxDate = new SbLocalDateTime(
        val.value.set({
          day: val.value.day,
          month: val.value.month,
          year: val.value.year,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
      );
    }
    if (this._maxDate && this._maxDate.value) {
      this._maxDateStruct = {
        year: this._maxDate.value.year,
        month: this._maxDate.value.month,
        day: this._maxDate.value.day,
      };
    }
  }

  @Input('maxDateStruct')
  set maxDateStruct(val: NgbDateStruct) {
    this._maxDateStruct = val;
  }

  get maxDateStruct(): NgbDateStruct {
    return this._maxDateStruct;
  }

  @Input('minDateStruct')
  set minDateStruct(val: NgbDateStruct) {
    this._minDateStruct = val;
  }

  get minDateStruct(): NgbDateStruct {
    return this._minDateStruct;
  }

  set dateString(value: string) {
    this._dateString = value;
  }

  get dateString(): string {
    return this._dateString;
  }

  set dateStruct(value: NgbDateStruct) {
    this._dateStruct = value;
    this.refreshFromDateStruct();
  }

  get dateStruct(): NgbDateStruct {
    return this._dateStruct;
  }

  @Input('model')
  set model(value: SbLocalDateTime) {
    this._model = value;
    this.refreshFromSetter();
  }

  get model(): SbLocalDateTime {
    return this._model;
  }

  visible: boolean;

  resetDateTime: SbLocalDateTime;

  placeholder: string = this.translate.instant('lang.globals.selectDate');

  isDisabled: any;

  @Input()
  title: string;

  @Input()
  weekNumbers = true;

  @Input()
  disablePicker: boolean;

  _enabledDates: SbLocalDate[];
  @Input('enabledDates')
  set enabledDates(val: SbLocalDate[]) {
    if (val) {
      if (this._enabledDates && this._enabledDates.length > 0) {
        if (this._enabledDates.length === val.length) {
          let differentArrays: boolean = false;
          for (let i = 1; i < val.length - 1; i++) {
            if (this._enabledDates[i].value !== val[i].value) {
              differentArrays = true;
            }
          }
          if (differentArrays) {
            this._enabledDates = val.slice();
            this.updateDisabledDates();
          }
        } else {
          this._enabledDates = val.slice();
          this.updateDisabledDates();
        }
      } else {
        this._enabledDates = val.slice();
        this.updateDisabledDates();
      }
    } else {
      this._enabledDates = [];
      this.updateDisabledDates();
    }
  }

  get enabledDates(): SbLocalDate[] {
    return this._enabledDates;
  }

  _minDate: SbLocalDateTime;

  _maxDate: SbLocalDateTime;

  _maxDateStruct: NgbDateStruct;

  _minDateStruct: NgbDateStruct;

  _dateString: string;

  _dateStruct: NgbDateStruct;

  _model: SbLocalDateTime;

  @Output() modelChange: EventEmitter<SbLocalDateTime> = new EventEmitter<SbLocalDateTime>();

  @HostListener('document:click', ['$event'])
  clickOut(e) {
    if (this.eRef.nativeElement.contains(e.target)) {
    } else {
      this.visible = false;
    }
  }

  ngOnInit(): void {}

  updateDisabledDates() {
    if (!this.enabledDates) {
      this.isDisabled = (d: NgbDate): boolean => {
        return false;
      };
    } else {
      this.isDisabled = (d: NgbDate): boolean => {
        return !this.checkInArray(d, this.enabledDates);
      };
    }
  }

  checkInArray(date: NgbDate, dates: SbLocalDate[]): boolean {
    for (let i = 0; i < dates.length; i++) {
      if (date.year === dates[i].value.year && date.month === dates[i].value.month && date.day === dates[i].value.day) {
        return true;
      }
    }
    return false;
  }

  clear() {
    this.model = null;
    this.modelChange.emit(this._model);
  }

  toggleDatepicker() {
    this.visible = !this.visible;
  }

  refreshReset() {
    this.resetDateTime = this.model;
  }

  reset() {
    if (!(this.model && this.model.value && this.model.value.isValid)) {
      if (this.resetDateTime && this.resetDateTime.value && this.resetDateTime.value.isValid) {
        this.model = this.resetDateTime;
      } else {
        this.model = null;
      }
    }
    this.modelChange.emit(this.model);
  }

  refreshFromDateString() {
    if (this.dateString && this.util.parseDateFromDateString(this.dateString)) {
      const dt: SbLocalDateTime = new SbLocalDateTime();
      dt.value = this.util.parseDateFromDateString(this.dateString);
      if (!this.checkInArray(new NgbDate(dt.value.year, dt.value.month, dt.value.day), this.enabledDates)) {
        this.toastHandler.toast(ToastType.DANGER, 'Date has no values');
      } else {
        if (this._minDate?.value && dt?.value && this._minDate.value.toMillis() > dt.value.toMillis()) {
          this.refreshFromDateStruct();
        } else {
          this._model = dt;
          this.refreshReset();
          this._dateStruct = {
            year: this.model.value.year,
            month: this.model.value.month,
            day: this.model.value.day,
          };
        }
      }
    } else {
      this._model = null;
      this._dateStruct = null;
      if (!this.dateString || this.dateString.length <= 0) {
        this.refreshReset();
      }
    }

    this.reset();

    this.modelChange.emit(this._model);
  }

  refreshFromDateStruct() {
    if (this.dateStruct) {
      const dt: SbLocalDateTime = new SbLocalDateTime();
      dt.value = DateTime.local();
      dt.value = dt.value.set({
        day: this._dateStruct.day,
        month: this.dateStruct.month,
        year: this.dateStruct.year,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      if (this._minDate && this._minDate.value && !this.disablePicker) {
        if (dt.value < this._minDate.value) {
          // FALSCH
          this.refreshFromSetter();
        } else {
          // RICHTIG
          this._model = dt;
          this.refreshReset();
          this._dateString = this.util.dateToDateString(this.model.value);
        }
      } else {
        // RICHTIG
        this._model = dt;
        this.refreshReset();
        this._dateString = this.util.dateToDateString(this.model.value);
      }
    } else {
      this._model = null;
      this._dateString = '';
    }

    this.reset();
    this.propagateChange(this.model);
    this.modelChange.emit(this._model);
  }

  refreshFromSetter() {
    if (this.model && this.model.value) {
      if (this._minDate && this._minDate.value && !this.disablePicker) {
        if (this.model.value < this._minDate.value) {
          // FALSCH
          const dt: SbLocalDateTime = new SbLocalDateTime(
            DateTime.local(
              this._minDate.value.year,
              this._minDate.value.month,
              this._minDate.value.day,
              this._minDate.value.hour,
              this._minDate.value.minute,
              this._minDate.value.second,
              this._minDate.value.millisecond
            )
          );
          this._model = dt;
        } else {
          // RICHTIG
          this._model = this.model;
        }
      } else {
        // RICHTIG
        this._model = this.model;
      }

      this.refreshReset();
      this._dateString = this.util.dateToDateString(this.model.value);

      this._dateStruct = {
        year: this.model.value.year,
        month: this.model.value.month,
        day: this.model.value.day,
      };
      this.refreshReset();
      this._dateString = this.util.dateToDateString(this.model.value);
    } else {
      this._dateStruct = null;
      this._dateString = '';
    }
  }

  writeValue(obj: any): void {
    if (obj) {
      this.model = obj;
    }
  }

  propagateChange = (_: any) => {};

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
